import ReportsDashboardGallery from './components/ReportsDashboardGallery';
import curriculumIcon from '@iconify-icons/ion/school';
import moodboardIcon from '@iconify/icons-fa-solid/theater-masks';
import myPeekavilleProgressIcon from '@iconify/icons-fa-solid/clipboard-check';
import studentCreations from '@iconify/icons-clarity/color-palette-solid';
import barChart from '@iconify-icons/bx/bar-chart';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CurriculumElementaryDashboard from './CurriculumElementaryDashboard';
import CurriculumMiddleSchoolDashboard from './CurriculumMiddleSchoolDashboard';
import MoodBoard from './ClassroomDashboard/MoodBoard';
import MoodboardMiddleSchool from './MoodboardMiddleSchool';
import StrengthsReport from './StrengthsReport';
import MoodboardSchoolAdmin from './AdministratorDashboard/MoodboardAdmin/MoodboardSchoolAdmin';
import MoodboardDistrictAdmin from './AdministratorDashboard/MoodboardAdmin/MoodboardDistrictAdmin';
import ConcerningEmotionsReport from './ConcerningEmotionsReport';
import ConcerningEmotionsReportAdmin from './AdministratorDashboard/ConcerningEmotionsReportAdmin';
import DistrictReport from './AdministratorDashboard/DistrictReport';
import SchoolReport from './AdministratorDashboard/SchoolReport/SchoolReport';
import Reseller from './Reports/Reseller';
import PeekavilleProgressReport from './ClassroomDashboard/PeekavilleProgressReport';
import AttendanceReport from './Reports/AttendanceReport';
import SpinnerOverlay from './SpinnerOverlay';
import type { RootState } from './ApplicationState';
import { isTakweenAttendanceEnabled, isConcerningEmotionsEnabled } from './BackendInterface';
import { isTakweenAccount } from './isTakweenAccount';

export const ugcCategories = {
  CurriculumReports: {
    representativeImage: curriculumIcon,
    onClickUrl: '/reports/Curriculum',
    title: 'Curriculum Reports',
    gradeSpan: [-1, 5],
    category: 'Teacher',
    description: 'View statistics on your Peekapak lesson completion and ELA standards completion.',
  },
  MiddleSchoolCurriculum: {
    representativeImage: curriculumIcon,
    onClickUrl: 'reports/CurriculumSecondary',
    title: 'Curriculum Reports',
    gradeSpan: [6, 12],
    category: 'Teacher',
    description: 'View statistics on your Peekapak lesson completion and ELA standards completion. ',
  },
  MiddleSchoolMood: {
    representativeImage: moodboardIcon,
    onClickUrl: 'reports/MoodboardSecondary',
    title: 'Moodboard',
    gradeSpan: [6, 12],
    category: 'Teacher',
    description: 'See at a glance how students report they are feeling using the emotions check-in tool.',
  },
  Moodboard: {
    representativeImage: moodboardIcon,
    onClickUrl: '/reports/Moodboard',
    title: 'Student Moodboard Reports',
    gradeSpan: [-1, 5],
    category: 'Teacher',
    description:
      'See at a glance how students are checking in on myPeekaville. View aggregate weekly class mood data as well.',
  },
  MoodboardSchoolAdmin: {
    representativeImage: moodboardIcon,
    onClickUrl: '/reports/MoodboardSchool',
    title: 'School Wide Moodboard',
    requiredKeys: ['KEY_ADMINISTRATOR_REPORTS'],
    restrictedKeys: ['KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL'],
    gradeSpan: [-1, 12],
    category: 'Admin',
    description: 'View schoolwide data on Peekapak feelings check-ins from students.',
  },
  MoodboardDistrictAdmin: {
    representativeImage: moodboardIcon,
    onClickUrl: '/reports/MoodboardDistrict',
    title: 'District Wide Moodboard',
    requiredKeys: ['KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL'],
    gradeSpan: [-1, 12],
    category: 'Admin',
    description: 'View districtwide data on Peekapak feelings check-ins from students.',
  },
  ConcerningEmotionsReport: {
    representativeImage: moodboardIcon,
    onClickUrl: '/reports/ConcerningEmotions',
    title: 'Concerning Emotions Report',
    gradeSpan: [-1, 12],
    category: 'Teacher',
    description: 'See which students may benefit from a caring check-in, based on their recent mood check-ins.',
  },
  ConcerningEmotionsReportAdmin: {
    representativeImage: moodboardIcon,
    onClickUrl: '/reports/ConcerningEmotionsAdmin',
    title: 'Concerning Emotions Report',
    requiredKeys: ['KEY_ADMINISTRATOR_REPORTS'],
    restrictedKeys: ['KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL'],
    gradeSpan: [-1, 12],
    category: 'Admin',
    description:
      'See schoolwide which students may benefit from a caring check-in, based on their recent mood check-ins.',
  },
  myPeekavilleProgressReports: {
    representativeImage: myPeekavilleProgressIcon,
    onClickUrl: '/reports/MyPeekaville',
    title: 'myPeekaville Progress Reports',
    gradeSpan: [-1, 5],
    category: 'Teacher',
    description:
      'Understand how students are progressing through the unit content on myPeekaville. View assessment data on the different learning games.',
  },
  UserGeneratedContentGallery: {
    representativeImage: studentCreations,
    onClickUrl: '/gallery',
    title: 'Gallery of Student Creations',
    gradeSpan: [-1, 5],
    category: 'Teacher',
    description:
      'View students’ artwork that they have created in myPeekaville! We recommend printing out and creating an SEL gallery wall in your classroom.',
  },
  SchoolReports: {
    representativeImage: barChart,
    onClickUrl: '/reports/School',
    title: 'School Reports',
    requiredKeys: ['KEY_ADMINISTRATOR_REPORTS'],
    gradeSpan: [-1, 12],
    category: 'Admin',
    description:
      'View schoolwide data on Peekapak usage. This includes registrations, top engaged teachers, top student downmoods, books read and more.',
  },
  DistrictReports: {
    representativeImage: barChart,
    onClickUrl: '/reports/District',
    title: 'District Reports',
    requiredKeys: ['KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL'],
    gradeSpan: [-1, 12],
    category: 'Admin',
    description:
      'View districtwide data on Peekapak usage. This includes number of logins and amount of storybook opens.',
  },
  ResellerReports: {
    representativeImage: barChart,
    onClickUrl: '/reports/Reseller',
    title: 'Reseller Reports',
    requiredKeys: ['KEY_RESELLER_REPORTS'],
    gradeSpan: [-1, 12],
    category: 'Reseller',
    description: `Get insight on your customers' use of Peekapak`,
  },
  StrengthsReports: {
    representativeImage: barChart,
    onClickUrl: '/reports/Strengths',
    title: 'Strengths Finder Reports',
    gradeSpan: [9, 12],
    category: 'Teacher',
    description: `Get insight into your students' strengths based on the use of the Strengths Finder tool.`,
  },
};

const ReportsDashboard = (): JSX.Element => {
  const { path } = useRouteMatch();

  const userProfile = useSelector((state: RootState) => state.user.userProfile);
  const isTakweenLicensedUser = isTakweenAccount(userProfile);
  const [isTakweenAttendanceReportEnabled, setIsTakweenAttendanceReportEnabled] = useState(false);
  const [isTakweenAttendanceReportToggleLoading, setIsTakweenAttendanceReportToggleLoading] =
    useState(isTakweenLicensedUser);

  useEffect(() => {
    let canceled = false;

    async function loadTakweenAttendanceReportToggle() {
      if (!isTakweenLicensedUser) {
        setIsTakweenAttendanceReportEnabled(false);
        setIsTakweenAttendanceReportToggleLoading(false);
        return;
      }

      setIsTakweenAttendanceReportToggleLoading(true);
      const districtId = userProfile?.district || '';
      const schoolId = userProfile?.schoolId || '';
      let enabled = false;

      try {
        enabled = await isTakweenAttendanceEnabled(districtId, schoolId);
      } finally {
        if (!canceled) {
          setIsTakweenAttendanceReportToggleLoading(false);
        }
      }

      if (!canceled) {
        setIsTakweenAttendanceReportEnabled(enabled);
      }
    }

    void loadTakweenAttendanceReportToggle();

    return () => {
      canceled = true;
    };
  }, [isTakweenLicensedUser, userProfile?.district, userProfile?.schoolId]);

  const canAccessTakweenAttendanceReport = isTakweenLicensedUser && isTakweenAttendanceReportEnabled;

  const [concerningEmotionsEnabled, setConcerningEmotionsEnabled] = useState(false);
  const [isConcerningEmotionsToggleLoading, setIsConcerningEmotionsToggleLoading] = useState(true);

  useEffect(() => {
    let canceled = false;
    const districtId = userProfile?.district || '';
    const schoolId = userProfile?.schoolId || '';

    setIsConcerningEmotionsToggleLoading(true);
    isConcerningEmotionsEnabled(districtId, schoolId).then((enabled) => {
      if (!canceled) {
        setConcerningEmotionsEnabled(enabled);
        setIsConcerningEmotionsToggleLoading(false);
      }
    });

    return () => {
      canceled = true;
    };
  }, [userProfile?.district, userProfile?.schoolId]);

  const categories = useMemo(() => {
    if (!canAccessTakweenAttendanceReport) {
      return ugcCategories;
    }

    return {
      ...ugcCategories,
      TakweenAttendanceReport: {
        representativeImage: myPeekavilleProgressIcon,
        onClickUrl: '/reports/Attendance',
        title: 'Attendance Reports',
        category: 'Teacher',
        description: 'Track classroom attendance patterns over time with present, absent, and attendance rate trends.',
      },
    };
  }, [canAccessTakweenAttendanceReport]);

  const visibleCategories = useMemo(() => {
    if (concerningEmotionsEnabled) {
      return categories;
    }

    const { ConcerningEmotionsReport: _report, ConcerningEmotionsReportAdmin: _reportAdmin, ...rest } = categories;
    return rest;
  }, [categories, concerningEmotionsEnabled]);

  return (
    <Switch>
      <Route exact path={`${path}`}>
        {isConcerningEmotionsToggleLoading ? (
          <SpinnerOverlay isShow />
        ) : (
          <ReportsDashboardGallery categories={visibleCategories} />
        )}
      </Route>
      <Route exact path={`${path}/Curriculum`}>
        <CurriculumElementaryDashboard />
      </Route>
      <Route exact path={`${path}/CurriculumSecondary`}>
        <CurriculumMiddleSchoolDashboard />
      </Route>
      <Route path={`${path}/Moodboard`}>
        <MoodBoard />
      </Route>
      <Route path={`${path}/MoodboardSecondary`}>
        <MoodboardMiddleSchool />
      </Route>
      <Route path={`${path}/MoodboardSchool`}>
        <MoodboardSchoolAdmin />
      </Route>
      <Route path={`${path}/MoodboardDistrict`}>
        <MoodboardDistrictAdmin />
      </Route>
      <Route path={`${path}/ConcerningEmotions`}>
        <ConcerningEmotionsReport />
      </Route>
      <Route path={`${path}/ConcerningEmotionsAdmin`}>
        <ConcerningEmotionsReportAdmin />
      </Route>
      <Route path={`${path}/MyPeekaville`}>
        <PeekavilleProgressReport />
      </Route>
      <Route path={`${path}/District`}>
        <DistrictReport />
      </Route>
      <Route path={`${path}/School`}>
        <SchoolReport />
      </Route>
      <Route path={`${path}/Reseller`}>
        <Reseller />
      </Route>
      <Route path={`${path}/Strengths`}>
        <StrengthsReport />
      </Route>
      <Route exact path={`${path}/Attendance`}>
        {isTakweenLicensedUser && isTakweenAttendanceReportToggleLoading ? (
          <SpinnerOverlay isShow />
        ) : canAccessTakweenAttendanceReport ? (
          <AttendanceReport />
        ) : (
          <Redirect to={path} />
        )}
      </Route>
    </Switch>
  );
};

export default ReportsDashboard;
